import { MESSAGE_IDS, type MessageId } from '@trust_network/shared/message-ids';
import { ApiError } from '../model/error';

type TranslateApiMessage = (messageId: MessageId | string) => string;

export function normalizeApiError(
  error: unknown,
  translateMessage: TranslateApiMessage
) {
  if (error instanceof ApiError) {
    return translateMessage(error.payload?.messageId ?? error.messageId);
  }

  if (error instanceof Error) {
    return translateMessage(error.message);
  }

  return translateMessage(MESSAGE_IDS.unknownError);
}

export function translateApiFieldErrors<TFieldErrors extends object>(
  fieldErrors: Record<string, string[] | undefined> | undefined,
  translateMessage: TranslateApiMessage
) {
  return Object.fromEntries(
    Object.entries(fieldErrors ?? {}).map(([field, messages]) => [
      field,
      messages?.[0] ? translateMessage(messages[0]) : undefined,
    ])
  ) as Partial<TFieldErrors>;
}

export function hasApiFieldErrors<TFieldErrors extends object>(
  fieldErrors: Partial<TFieldErrors>
) {
  return Object.values(fieldErrors).some(Boolean);
}

export function getApiActionErrorResult<TFieldErrors extends object>(
  error: unknown,
  translateMessage: TranslateApiMessage,
  fieldMap: Partial<Record<MessageId | string, keyof TFieldErrors>> = {}
) {
  if (error instanceof ApiError) {
    const translatedFieldErrors = translateApiFieldErrors<TFieldErrors>(
      error.payload?.issues?.fieldErrors,
      translateMessage
    );

    if (hasApiFieldErrors(translatedFieldErrors)) {
      return {
        success: false as const,
        fieldErrors: translatedFieldErrors as TFieldErrors,
      };
    }

    const mappedField = fieldMap[error.payload?.messageId ?? error.messageId];
    if (mappedField) {
      return {
        success: false as const,
        fieldErrors: {
          [mappedField]: normalizeApiError(error, translateMessage),
        } as TFieldErrors,
      };
    }
  }

  return {
    success: false as const,
    formError: normalizeApiError(error, translateMessage),
  };
}
